/**
 * THE LIVE BOARD, BUILT ONCE (Tim, 8 September: "impersonating matt and his pick rundown should
 * include his keepers/farm/franchised players and so should his roster").
 *
 * Keepers are not saved picks. They are DERIVED — from each GM's declaration, the Grid's cost
 * rounds, the rounds he actually holds after trades, and the escalation rule that pushes a
 * collision earlier (2015-III-7). The draft board did that derivation inside its own component
 * and every other screen read the raw `picks` collection instead, so Home showed a man his
 * rundown with sixteen empty rows and an empty roster while the board beside it had four of his
 * keepers on it.
 *
 * So the derivation lives here and the screens call it. What comes back is the board the room
 * sees: keepers and farm graduates placed on the picks they cost, real picks written over the
 * top, and every cell owned by whoever holds it after the ledger's trades.
 */

import keeperRows from '../data/keepers-2026.json';
import { LEAGUE_2026 } from './config.js';
import { applyKeepers, buildBoard, type BoardPick } from './board.js';
import { assignKeeperPicks } from './draftOrder.js';
import { gmOfTeam } from './identity.js';
import { ownedRoundsFor } from './picks2026.js';
import type { KeeperDeclaration } from '../store/types.js';

const ROUNDS = LEAGUE_2026.draftRounds;

/** Team name → manager, with any seat change made in the app applied on top. */
const GM_OF = new Proxy({} as Record<string, string>, {
  get: (_t, team) => (typeof team === 'string' ? gmOfTeam(team) : undefined),
});

interface KeeperRow {
  player: string; team: string; pos: string; cost: number | null; tagCost: number | null;
  nflTeam: string | null;
  /** Came up through the farm system — the board frames him in violet rather than black. */
  farm?: boolean;
  /** Farm graduate in his rookie year, whom 2013-III-1 §5 makes a compulsory keeper. */
  mandatory?: boolean;
}
const KEEPERS = keeperRows as unknown as KeeperRow[];

/**
 * What each GM's declaration costs him, as concrete rounds. Same resolution the declaration
 * screen runs — collisions and traded picks push a keeper EARLIER (2015-III-7), and the GM's own
 * pins win where he set them.
 */
export interface SlateRow {
  player: string;
  /** What the Grid charges. The ceiling on where he may be placed, never the floor. */
  costRound: number;
  /** The GM's own saved choice, from his declaration. A board pin outranks it. */
  preferred: number | null;
  pos?: string;
  team?: string;
  /** A farm graduate: the board frames him in violet. */
  farm?: boolean;
}

/**
 * Each GM's keepers and the picks he actually holds — everything a placement decision needs,
 * separated from the placement itself.
 *
 * SPLIT OUT because dragging a keeper on the board asks a question the assignment cannot answer:
 * not "where did they land" but "where COULD this one go, and who is in the way". That needs the
 * costs and the owned rounds, which used to be locals inside the loop below.
 */
export function keeperContexts(
  declarations: readonly KeeperDeclaration[],
  ownership: ReadonlyMap<string, string>,
  rounds: number = ROUNDS,
): Record<string, { rows: SlateRow[]; ownedRounds: number[] }> {
  const out: Record<string, { rows: SlateRow[]; ownedRounds: number[] }> = {};
  for (const d of declarations) {
    const gm = d.gm.toUpperCase();
    const owned = ownedRoundsFor(gm, ownership, rounds);
    /*
      A FORFEITED GRADUATE OCCUPIES NO PICK. 2019-II-5 §1 takes the keeper spot and charges the
      tax as though he were kept; it does not say the draft pick is spent too, and he is not on
      anybody's roster to be drafted onto. So the pick goes back on the board and the GM uses it.

      OPEN QUESTION FOR THE COMMITTEE: this is the coherent reading rather than a stated one. If
      the league decides the pick is forfeited as well, this filter comes out and the cell stays
      his, drawn as spent.
    */
    const wanted = d.keepers.filter((k) => !k.farmForfeited).map((k) => {
      const row = KEEPERS.find(
        (x) => x.player === k.player && (GM_OF[x.team] ?? x.team.toUpperCase()) === gm,
      );
      const cost = k.franchiseTagged ? row?.tagCost ?? null : row?.cost ?? null;
      return { k, row, cost };
    }).filter((x) => x.cost !== null);

    out[gm] = {
      ownedRounds: owned,
      rows: wanted.map((w) => ({
        player: w.k.player,
        costRound: w.cost!,
        preferred: w.k.elevatedTo ?? null,
        pos: w.row?.pos,
        team: w.row?.nflTeam ?? undefined,
        farm: w.row?.farm === true,
      })),
    };
  }
  return out;
}

/**
 * Where each keeper lands, given the contexts and whatever the board has been dragged into.
 *
 * `pins` is gm -> player -> round and outranks the declaration's own `elevatedTo`, because a pin
 * here is somebody moving him just now. It is layered rather than written through: the board is
 * derived from declarations, and a drag is an edit to a declaration, not to the board.
 */
export function keeperAssignments(
  contexts: Readonly<Record<string, { rows: SlateRow[]; ownedRounds: number[] }>>,
  pins: Readonly<Record<string, Readonly<Record<string, number>>>> = {},
  dropped: Readonly<Record<string, readonly string[]>> = {},
) {
  const out: Record<string, Array<
    { player: string; round: number; pos?: string; team?: string; farm?: boolean }
  >> = {};
  for (const [gm, ctx0] of Object.entries(contexts)) {
    const mine = pins[gm] ?? {};
    const gone = new Set(dropped[gm] ?? []);
    const ctx = { ...ctx0, rows: ctx0.rows.filter((r) => !gone.has(r.player)) };
    const { assigned } = assignKeeperPicks(
      ctx.rows.map((r) => ({
        id: r.player, costRound: r.costRound, preferred: mine[r.player] ?? r.preferred,
      })),
      ctx.ownedRounds,
    );
    out[gm] = ctx.rows
      .filter((r) => assigned.has(r.player))
      .map((r) => ({
        player: r.player,
        round: assigned.get(r.player)!,
        pos: r.pos,
        team: r.team,
        farm: r.farm,
      }));
  }
  return out;
}

export interface LiveBoardInput {
  order: readonly string[];
  rounds?: number;
  ownership: ReadonlyMap<string, string>;
  /** Every GM's slate. Submitted only — a draft is not a thing the board may place. */
  declarations: readonly KeeperDeclaration[];
  /** The saved picks, by overall. A real pick written on a cell replaces whatever was on it. */
  saved?: ReadonlyMap<number, { player?: string; position?: string; madeAt?: string; madeBy?: string }>;
  /** A keeper being dragged on the board, and one dropped from a slate — the board's own state. */
  pins?: Readonly<Record<string, Readonly<Record<string, number>>>>;
  dropped?: Readonly<Record<string, readonly string[]>>;
}

/**
 * The board every screen should be reading: cells owned after trades, keepers and farm graduates
 * placed where they cost, and the picks actually made written over the top.
 */
export function liveBoard(
  { order, rounds = ROUNDS, ownership, declarations, saved, pins = {}, dropped = {} }: LiveBoardInput,
): { board: BoardPick[]; errors: string[] } {
  if (!order.length) return { board: [], errors: [] };
  const empty = buildBoard(order, rounds, ownership);
  const submitted = declarations.filter((d) => d.status === 'submitted');
  const withKeepers = applyKeepers(
    empty, keeperAssignments(keeperContexts(submitted, ownership, rounds), pins, dropped),
  );
  if (!saved) return withKeepers;
  const board = withKeepers.board.map((p) => {
    const s = saved.get(p.overall);
    return s?.player ? { ...p, ...s, keeper: false } as BoardPick : p;
  });
  return { board, errors: withKeepers.errors };
}
